import type { ContributionSlice } from "@/analytics/core/types";
import { formatMetricForCell } from "@/grid/core/format-metric";
import type { MetricFormat } from "@/grid/core/types";
import { cn } from "@/lib/cn";
import { useMemo } from "react";

interface ExploreContributionPieProps {
  slices: ContributionSlice[];
  format: MetricFormat;
  className?: string;
  /** SVG width/height in px. Default 88 (Explore KPI). */
  size?: number;
  /** When false, rep names wrap instead of truncating (stacked layouts). */
  truncateLabels?: boolean;
  /** Render pie only, legend only, or both (default). */
  part?: "all" | "pie" | "legend";
  /** Legend rows under the pie (team tiles) vs beside it (Explore). */
  legendLayout?: "inline" | "stacked";
}

function polarToCartesian(cx: number, cy: number, radius: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function describePieSlice(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string {
  if (endAngle - startAngle >= 360) {
    return `M ${cx - radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx + radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx - radius} ${cy} Z`;
  }

  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;

  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

export function ExploreContributionPie({
  slices,
  format,
  className,
  size = 88,
  truncateLabels = true,
  part = "all",
  legendLayout = "inline",
}: ExploreContributionPieProps) {
  const radius = Math.round(size * (40 / 88));
  const cx = size / 2;
  const cy = size / 2;

  const total = useMemo(() => slices.reduce((sum, slice) => sum + slice.value, 0), [slices]);

  const arcs = useMemo(() => {
    if (total <= 0) return [];

    let cursor = 0;
    return slices
      .filter((slice) => slice.value > 0)
      .map((slice) => {
        const sweep = (slice.value / total) * 360;
        const start = cursor;
        const end = cursor + sweep;
        cursor = end;
        return { slice, start, end };
      });
  }, [slices, total]);

  if (slices.length < 2) return null;

  const stackedLegend = legendLayout === "stacked";

  const pie = (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="Contribution breakdown"
      className="shrink-0"
    >
      <title>Contribution breakdown</title>
      {total <= 0 ? (
        <circle cx={cx} cy={cy} r={radius} fill="var(--color-surface-muted)" />
      ) : (
        arcs.map(({ slice, start, end }) => (
          <path
            key={slice.key}
            d={describePieSlice(cx, cy, radius, start, end - 0.35)}
            fill={slice.color}
            stroke="white"
            strokeWidth={1.5}
          >
            <title>
              {slice.label}: {slice.sharePct}% · {formatMetricForCell(slice.value, format)}
            </title>
          </path>
        ))
      )}
    </svg>
  );

  const legend = (
    <ul
      className={cn(
        stackedLegend
          ? "w-full space-y-2"
          : cn("min-w-0 flex-1 space-y-1.5", !truncateLabels && "space-y-2"),
      )}
    >
      {slices.map((slice) => (
        <li
          key={slice.key}
          className={cn(
            stackedLegend
              ? "flex items-center justify-between gap-4 text-sm leading-snug"
              : "flex items-baseline gap-2 text-xs leading-snug sm:text-sm",
          )}
        >
          <span className={cn("flex min-w-0 items-center gap-2", stackedLegend && "shrink-0")}>
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: slice.color }}
              aria-hidden
            />
            <span
              className={cn(
                "text-[var(--color-text)]",
                stackedLegend ? "whitespace-nowrap" : cn("min-w-0", truncateLabels && "truncate"),
              )}
            >
              {slice.label}
            </span>
          </span>
          <span className="flex shrink-0 items-baseline gap-3 tabular-nums text-[var(--color-text-muted)]">
            <span>{slice.sharePct}%</span>
            <span>{formatMetricForCell(slice.value, format)}</span>
          </span>
        </li>
      ))}
    </ul>
  );

  if (part === "pie") {
    return <div className={className}>{pie}</div>;
  }

  if (part === "legend") {
    return <div className={cn("w-full", className)}>{legend}</div>;
  }

  if (stackedLegend) {
    return (
      <div className={cn("flex flex-col items-center gap-4", className)}>
        {pie}
        {legend}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-3", className)}>
      {pie}
      {legend}
    </div>
  );
}
